import type { StyleLibrary, UploadedFont } from "schemas/generated/project";

import { referralCodeSchema } from "schemas/referralCode";
import { reploSymbolSchema } from "schemas/symbol";
import { z } from "zod";

export type CurrencyCode =
  | "AED"
  | "AUD"
  | "CAD"
  | "CHF"
  | "DKK"
  | "EUR"
  | "GBP"
  | "NZD"
  | "QAR"
  | "SAR"
  | "SGD"
  | "BRL"
  | "MXN"
  | "NOK"
  | "RON"
  | "JPY"
  | "PLN"
  | "INR"
  | "HUF"
  | "SEK"
  | "ILS"
  | "CZK"
  | "HKD"
  | "ISK"
  | "PHP"
  | "RSD"
  | "BGN"
  | "IDR"
  | "THB"
  | "TWD"
  | "USD";

export const CURRENCIES: Record<CurrencyCode, { name: string }> = {
  AED: { name: "UAE Dirham" },
  AUD: { name: "Australian Dollar" },
  BGN: { name: "Bulgarian Lev" },
  BRL: { name: "Brazilian Real" },
  CAD: { name: "Canadian Dollar" },
  CHF: { name: "Swiss Franc" },
  CZK: { name: "Czech Koruna" },
  DKK: { name: "Danish Krone" },
  EUR: { name: "Euro" },
  GBP: { name: "British Pound" },
  HKD: { name: "Hong Kong Dollar" },
  HUF: { name: "Hungarian Forint" },
  IDR: { name: "Indonesian Rupiah" },
  ILS: { name: "Israeli New Shekel" },
  INR: { name: "Indian Rupee" },
  ISK: { name: "Icelandic Króna" },
  JPY: { name: "Japanese Yen" },
  MXN: { name: "Mexican Peso" },
  NOK: { name: "Norwegian Krone" },
  NZD: { name: "New Zealand Dollar" },
  PHP: { name: "Philippine Peso" },
  PLN: { name: "Polish Złoty" },
  QAR: { name: "Qatari Riyal" },
  RON: { name: "Romanian Leu" },
  RSD: { name: "Serbian Dinar" },
  SAR: { name: "Saudi Riyal" },
  SEK: { name: "Swedish Krona" },
  SGD: { name: "Singapore Dollar" },
  THB: { name: "Thai Baht" },
  TWD: { name: "New Taiwan Dollar" },
  USD: { name: "United States Dollar" },
} as const;

export const currencySchema = z
  .enum(Object.keys(CURRENCIES) as [CurrencyCode, ...CurrencyCode[]])
  .describe("Currency");

/**
 * Returns the default StyleLibrary configuration for new projects
 */
export function getDefaultStyleLibrary(): StyleLibrary {
  return {
    colors: {
      neutrals: {
        light: "#FFFFFF",
        subtle: "#F5F5F5",
        muted: "#999999",
        dark: "#000000",
      },
      brand: {
        primary: {
          color: "#000000",
          name: "Primary",
        },
        secondary: {
          color: "#FFFFFF",
          name: "Secondary",
        },
        tertiary: {
          color: "",
          name: "",
        },
        accent: {
          color: "",
          name: "",
        },
      },
    },
    typography: {
      h1: {
        name: "Aeonik",
        url: "",
        size: 48,
        weight: 700,
        lineHeight: 0,
        letterSpacing: 0,
      },
      h2: {
        name: "Aeonik",
        url: "",
        size: 32,
        weight: 500,
        lineHeight: 0,
        letterSpacing: 0,
      },
      h3: {
        name: "Aeonik",
        url: "",
        size: 28,
        weight: 500,
        lineHeight: 0,
        letterSpacing: 0,
      },
      h4: {
        name: "Aeonik",
        url: "",
        size: 24,
        weight: 500,
        lineHeight: 0,
        letterSpacing: 0,
      },
      body: {
        name: "Inter",
        url: "",
        size: 16,
        weight: 400,
        lineHeight: 0,
        letterSpacing: 0,
      },
    },
    cornerRounding: 8,
  };
}

export const uploadedFontSchema = z
  .object({
    name: z.string(),
    displayName: z.string().optional(),
    type: z.enum(["woff", "woff2"]),
    url: z.string(),
    weight: z.string().optional(),
    style: z.string().optional(),
  })
  .describe("UploadedFont");

export const inUseFontSchema = z
  .object({
    name: z.string(),
    url: z.string(),
    size: z.number(),
    weight: z.number(),
    lineHeight: z.number(),
    letterSpacing: z.number(),
  })
  .describe("InUseFont");

export const inUseFontsSchema = z.array(inUseFontSchema).describe("InUseFonts");

export const shopifyStoreSchema = z
  .object({
    integrationId: z.string().nullish(),
    shopifyIntegrationConfigId: z.string().nullish(),
    shopifyUrl: z.string(),
    url: z.string().nullish(),
    contactEmail: z.string().nullish(),
    shopifyAccessScopes: z.string().nullish(),
    shopifyStorePassword: z.string().nullish(),
  })
  .describe("ShopifyStore");

export const styleLibrarySchema = z
  .object({
    colors: z.object({
      neutrals: z.object({
        light: z.string(),
        subtle: z.string(),
        muted: z.string(),
        dark: z.string(),
      }),
      brand: z.object({
        primary: z.object({
          color: z.string(),
          name: z.string(),
        }),
        secondary: z.object({
          color: z.string(),
          name: z.string(),
        }),
        tertiary: z.object({
          color: z.string(),
          name: z.string(),
        }),
        accent: z.object({
          color: z.string(),
          name: z.string(),
        }),
      }),
    }),
    typography: z.object({
      h1: z.object({
        name: z.string(),
        url: z.string(),
        size: z.number(),
        weight: z.number(),
        lineHeight: z.number(),
        letterSpacing: z.number(),
      }),
      h2: z.object({
        name: z.string(),
        url: z.string(),
        size: z.number(),
        weight: z.number(),
        lineHeight: z.number(),
        letterSpacing: z.number(),
      }),
      h3: z.object({
        name: z.string(),
        url: z.string(),
        size: z.number(),
        weight: z.number(),
        lineHeight: z.number(),
        letterSpacing: z.number(),
      }),
      h4: z.object({
        name: z.string(),
        url: z.string(),
        size: z.number(),
        weight: z.number(),
        lineHeight: z.number(),
        letterSpacing: z.number(),
      }),
      body: z.object({
        name: z.string(),
        url: z.string(),
        size: z.number(),
        weight: z.number(),
        lineHeight: z.number(),
        letterSpacing: z.number(),
      }),
    }),
    cornerRounding: z.number(),
  })
  .describe("StyleLibrary");

export const reploProjectSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().nullish(),
    shopDomainPrefix: z.string().nullish(),
    shortName: z.string().nullish(),
    createdAt: z.date(),
    referrerKey: z.string().nullish(),
    // NOTE (Fran 2024-06-07): revenue share is calculated manually in the
    // workspaces tab in dashboard
    referrerCode: referralCodeSchema
      .omit({ revenueSharePercentage: true })
      .nullish()
      .describe("ignoreImport:true"),
    tenancy: z.string().nullish(),
    uploadedFonts: z.array(uploadedFontSchema).nullish(),
    ownerId: z.string().uuid().nullish(),
    ownerWorkspaceId: z.string().uuid(),
    ownerWorkspace: z.object({
      name: z.string(),
      domain: z.string(),
      id: z.string().nullish(),
    }),
    timeZone: z.string().nullish(),
    currency: currencySchema.nullish(),
    productDescription: z.string().nullish(),
    symbols: z.array(reploSymbolSchema).nullish(),
    script: z.string().nullish(),
    styleLibrary: styleLibrarySchema.nullish(),
    inUseFonts: inUseFontsSchema.nullish(),
    componentsCss: z.string().nullish(),
    urlHeuristics: z.string().nullish(),
    shopMetadataLightModeFaviconUrl: z.string().nullish(),
    shopMetadataDarkModeFaviconUrl: z.string().nullish(),
    integrations: z
      .object({
        shopify: z
          .object({
            store: shopifyStoreSchema,
          })
          .nullish(),
      })
      .nullish(),
  })
  .describe("ReploProject");

export const reploSimpleProjectSchema = reploProjectSchema
  .pick({
    id: true,
    name: true,
    ownerWorkspaceId: true,
    ownerWorkspace: true,
    ownerId: true,
    createdAt: true,
    integrations: true,
    uploadedFonts: true,
    script: true,
    timeZone: true,
    currency: true,
  })
  .extend({
    lastEditedAt: z.date().optional(),
  })
  .describe("ReploSimpleProject");

export const reploProjectWithNoIntegrationsSchema = reploProjectSchema
  .omit({
    integrations: true,
  })
  .describe("ReploProjectWithNoIntegrations");

export const reploSimpleProjectWithStatsSchema = reploSimpleProjectSchema
  .extend({
    elementCounts: z.object({
      pageCount: z.number(),
      productTemplateCount: z.number(),
      sectionCount: z.number(),
      blogCount: z.number(),
    }),
  })
  .describe("ReploSimpleProjectWithStats");

export function isUploadedFont(value: unknown): value is UploadedFont {
  return (
    value != null &&
    typeof value === "object" &&
    "name" in value &&
    typeof value.name === "string" &&
    "type" in value &&
    ["woff", "woff2"].includes(value.type as string) &&
    "url" in value &&
    typeof value.url === "string"
  );
}
